import { Box, Text, colors } from '@factorialco/deprecated-design-system'
import { partition } from 'lodash'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { isTerminalStatus } from 'modules/payroll_bundle/lib/Incidences/Status'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import type { IncidenceChangeItem } from 'modules/payroll_bundle/lib/types'

import IncidenceHistoryItem from 'modules/payroll_bundle/pages/IncidenceHistoryPage/_IncidenceHistoryItem'
import styles from 'modules/payroll_bundle/pages/IncidenceHistoryPage/index.module.scss'
import TagSeparator from './_TagSeparator'

interface ChangeItemProps {
  change: IncidenceChangeItem
  isLast: boolean
  isManualIncidence: boolean
}

const ChangeItem = ({ change, isLast, isManualIncidence }: ChangeItemProps) => {
  const date = change.effectiveAt.format('D MMM YY')
  const hour = change.effectiveAt.format('H:mm')
  const currentValue = change.currentValue
  const previousValue = change.previousValue

  return (
    <Box flexDirection="row">
      <Box
        textAlign="right"
        paddingBottom="s32"
        paddingRight="s24"
        flexNoShrink
        className={styles.timeInformation}
      >
        <Text.Normal size="100" weight="semibold" capitalize>
          {date}
        </Text.Normal>
        <Text.Normal size="100">{hour}</Text.Normal>
      </Box>
      <IncidenceHistoryItem
        isLast={isLast}
        change={change}
        currentValue={currentValue}
        previousValue={previousValue}
        timelineDot={styles.timelineDot}
        isManualIncidence={isManualIncidence ?? false}
      />
    </Box>
  )
}

const ChangeItemList = ({ changeItems }: ChangeItemList) => {
  const incidence = useCurrentIncidence()
  const incidenceCreatedAt = incidence?.createdAt
  const filteredChangeItems = changeItems.filter((change: IncidenceChangeItem) => {
    return moment(change.effectiveAt).isSameOrAfter(incidenceCreatedAt)
  })

  return (
    <>
      {filteredChangeItems.map((change: IncidenceChangeItem, index: number) => {
        return (
          <ChangeItem
            key={`${index}-${change.id}`}
            change={change}
            isLast={filteredChangeItems.length - 1 === index}
            isManualIncidence={incidence?.name === INCIDENCE_TYPES.manual_incidence}
          />
        )
      })}
    </>
  )
}

interface ChangeItemList {
  changeItems: IncidenceChangeItem[]
}

const IncidenceHistoryList = ({ changeItems }: ChangeItemList) => {
  const incidence = useCurrentIncidence()
  const finalIncidenceStatuses = changeItems.filter((change) => {
    return (
      change.id === 'status_updated' &&
      typeof change.currentValue?.value === 'string' &&
      isTerminalStatus(change.currentValue.value)
    )
  })

  const lastReopeningDate = finalIncidenceStatuses.pop()?.effectiveAt

  const [changesAfterLastReopening, changesBeforeLastReopening] = partition(
    changeItems,
    (change: IncidenceChangeItem) =>
      lastReopeningDate ? change.effectiveAt > lastReopeningDate : false
  )

  const splitChanges: boolean =
    incidence.isReopened &&
    changesAfterLastReopening.length > 0 &&
    changesBeforeLastReopening.length > 0

  return (
    <>
      {splitChanges ? (
        <>
          <TagSeparator
            type="malibu"
            label={i18n.t('bookkeepers.incidence.historic_changes.section.new')}
            colorLine={colors.malibu1000}
            solid
          />
          <ChangeItemList changeItems={changesAfterLastReopening.reverse()} />
          <TagSeparator
            type="grey"
            label={i18n.t('bookkeepers.incidence.historic_changes.section.old')}
            colorLine={colors.grey600}
          />
          <ChangeItemList changeItems={changesBeforeLastReopening.reverse()} />
        </>
      ) : (
        <ChangeItemList changeItems={changeItems.reverse()} />
      )}
    </>
  )
}

export default IncidenceHistoryList
